import React, { useContext, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';

import { usePopper, Icon, Dropdown, ToolTip } from '@testsigma/atoms';
import { UserPreferenceContext } from '@contexts/user-preference-context';

import { ApplicationVersion } from '@models/application-version.model';
import { Application } from '@models/application.model';
import { Project } from '@models/project.model';

import { useQueryShowApplication } from '@services/application.service';
import { useQueryShowProject } from '@services/project.service';
import { useQueryShowVersion } from '@services/version.service';
import StrollEventService from '@services/strollr-event.service';

import SwitcherContainer from '@modules/project-switcher';
import { PROJECT_TYPE } from '@modules/project-switcher/constants';

interface projectSwitcherProps {
  isPrimaryMenuExpanded?: boolean;
  setIsSidebarhovered?: (flag: boolean) => void;
  toggleProjectSettings?: (flag: boolean) => void;
  setIsModelOpen: (flags: boolean) => void;
}

const ProjectSwitcher: React.FC<projectSwitcherProps> = ({
  isPrimaryMenuExpanded,
  setIsSidebarhovered,
  toggleProjectSettings,
  setIsModelOpen,
}) => {
  const userPreferenceContext = useContext(UserPreferenceContext);
  const projectPreference =
    userPreferenceContext?.userPreference?.preference?.projectPreference;

  const [showDropdown, toggleDropdown] = useState(false);
  const [dropdownRef, setDropdownRef] = useState(null);
  const [btnRef, setBtnRef] = useState(null);

  const offsetModifier = [
    {
      name: 'offset',
      options: {
        offset: [0, 4],
      },
    },
  ];
  const { styles, attributes } = usePopper(btnRef, dropdownRef, {
    placement: 'right-start',
    strategy: 'fixed',
    modifiers: offsetModifier,
  });

  //fetching default data
  const { data: project, isFetching: isFetchingProjects } = useQueryShowProject({
    projectId: projectPreference.projectId, 
    enabled: Boolean(projectPreference.projectId),
    onSuccess: (res: Project) => userPreferenceContext.setSelectedProject(res)
  });

  const {data: application} = useQueryShowApplication({
    applicationId: projectPreference.applicationId,
    enabled: Boolean(projectPreference.applicationId),
    onSuccess: (res: Application) => userPreferenceContext.setSelectedApplication(res)
  });

  const {data: version} = useQueryShowVersion({
    versionId: projectPreference.versionId,
    enabled: Boolean(projectPreference.versionId),
    onSuccess: (res: ApplicationVersion) => userPreferenceContext.setSelectedVersion(res)
  });

  const updateApplication = useCallback(
    (data: Application) => {
      if (!window.tsGlobal) {
        window.tsGlobal = {};
      }
      window.tsGlobal.applicationType = data?.applicationType;
      StrollEventService.trackUserProperty({
        applicationType: data?.isMobileNative ? 'Mobile' : 'Web',
      });
      userPreferenceContext.setSelectedApplication(data);
    },
    [projectPreference],
  );

  useEffect(() => {
    if(!projectPreference?.versionId){
      return userPreferenceContext.updateDefaultForProjectId({
        projectId: projectPreference.projectId,
        applicationId: projectPreference.applicationId,
      })
    }
    
    if(!projectPreference?.applicationId || !projectPreference?.projectId){
      return userPreferenceContext.fetchVersionToUpdatePreference({versionId: projectPreference.versionId});
    }
  },[
    projectPreference?.versionId,
    projectPreference?.applicationId,
    projectPreference?.projectId, 
  ])

  useEffect(() => {
    setIsModelOpen(showDropdown);
    if(!showDropdown){
      setIsSidebarhovered(false);
    }
  }, [showDropdown]);

  return (
    <div
      className={classNames(
        isPrimaryMenuExpanded ? 'px-lg' : 'justify-center',
        'flex flex-wrap basis-full w-full',
      )}>
      <ToolTip
        placement='bottom'
        content={
          <div className='block leading-5 w-52 break-all px-1'>
            {application?.name || '-'} - {version?.name || ''}
          </div>
        }
        className={classNames(
          'inline-flex overflow-hidden w-full justify-center',
          {
            'px-1 py-2.5': isPrimaryMenuExpanded,
          },
        )}>
        {isPrimaryMenuExpanded ? (
          <div
            onClick={() => (toggleDropdown(!showDropdown))}
            className={classNames(
              'text-unique-600 leading-5 w-full rounded-lg p-2 mb-md basis-full font-medium cursor-pointer flex flex-wrap items-center shadow hover:bg-secondary-50 bg-secondary-unique-1000',
              { '!bg-primary-1000 text-white': showDropdown },
            )}
            ref={setBtnRef}
            >
              <div className='flex items-center overflow-hidden'>
                <Icon
                  className={classNames(
                    'pr-[12px] text-[18px]',
                    showDropdown ? 'text-white' : 'text-unique-800',
                  )}
                  name={PROJECT_TYPE.icon[application?.applicationType]}
                />
                <span
                  className={classNames(
                    'text-[13px] truncate',
                    showDropdown ? 'text-white' : 'text-unique-600',
                  )}>
                  {isFetchingProjects ? "Loading ..." : project?.name}
                </span>
              </div>
          </div>
        ) : (
          <div
            ref={setBtnRef}
            onClick={() => toggleDropdown(!showDropdown)}
            className='text-secondary-800 leading-5 rounded-lg p-2 py-2.5 border border-neutral-50 mb-md font-medium cursor-pointer flex items-center shadow hover:bg-secondary-100 bg-secondary-unique-1000'>
            <Icon className='text-[18px]' name='nav-bar-menu' />
          </div>
        )}
      </ToolTip>
      {showDropdown && (
        <Dropdown
          className='z-[1000] !left-2 !overflow-visible !max-h-[385px] '
          toggleDropdown={toggleDropdown}
          setRef={setDropdownRef}
          style={styles.popper}
          attributes={attributes.popper}
          buttonRef={btnRef}>
          <SwitcherContainer
            toggleDropdown={toggleDropdown}
            project={project}
            application={application}
            version={version}
            updateApplicationCallBack={updateApplication}
            updateProjectCallBack={(data) => userPreferenceContext.setSelectedProject(data)}
            updateVersionCallBack={(data) =>  userPreferenceContext.setSelectedVersion(data)}
            setIsSidebarhovered={setIsSidebarhovered}
            toggleProjectSettings={toggleProjectSettings}
          />
        </Dropdown>
      )}
    </div>
  );
};

export default ProjectSwitcher;
